import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Typography, Container, Paper, CircularProgress, Snackbar, Alert, useTheme, useMediaQuery, keyframes } from "@mui/material";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuthService } from "../services/authService.jsx";
import { useUserData } from "../services/userDataService.jsx"; // To potentially refetch data
import { updateUserSubscribed } from "../services/stripeService.js";
import Header from '../components/Header';
import Footer from '../components/Footer';
import backdrop from "../img/backdrop.png"; // Reusing backdrop
import iosBadge from "../img/ios-badge.svg";
import googlePlayBadge from "../img/googleplay-badge.png";
import appHands from "../img/app-hands.png"; // Import the app-hands image
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.05);
    filter: brightness(1.2);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

function PaymentSuccess() {
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuthService();
  const { refetch: refetchUserData } = useUserData(); // Get refetch function
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const processedSessionRef = useRef(false); // Prevent double processing
  const [isLoadingPage, setIsLoadingPage] = useState(true); // Loading state for this page
  const [statusMessage, setStatusMessage] = useState('Processing payment...'); // User-facing status
  const [isError, setIsError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount

    const processPayment = async () => {
      // Wait for authentication check to complete
      if (isAuthLoading) {
        console.log("PaymentSuccess: Waiting for auth...");
        return; 
      }

      // Ensure user is authenticated
      if (!isAuthenticated || !user?.userId) {
        console.log("PaymentSuccess: User not authenticated, redirecting to login.");
        navigate('/login?redirect=/paymentsuccess' + location.search); // Redirect to login, preserving session_id
        return;
      }

      const query = new URLSearchParams(location.search);
      const sessionId = query.get('session_id');

      // Only process if we have a session ID and haven't processed it yet
      if (sessionId && !processedSessionRef.current) {
        console.log(`PaymentSuccess: Processing subscription for user ${user.userId} with session ${sessionId}`);
        processedSessionRef.current = true; // Mark as processed
        setIsLoadingPage(true);
        setStatusMessage('Updating your subscription status...');
        setIsError(false);

        // Remove session_id from URL bar for cleanliness
        window.history.replaceState({}, document.title, location.pathname);

        try {
            // **HERE: Call Lambda to update DynamoDB**
            const result = await updateUserSubscribed(user.userId);
            console.log('PaymentSuccess: User subscription updated:', result);
            
            setStatusMessage('Welcome to Screamify™! Thank you for supporting indie horror.');
            setIsError(false);
            setSnackbarOpen(true); // Show success snackbar
            
            // Refetch user data from DynamoDB after a short delay
            if (refetchUserData) {
                setTimeout(() => {
                    refetchUserData();
                    console.log('PaymentSuccess: User data refreshed after subscription update');
                }, 1000); 
            }

        } catch (error) {
            console.error('PaymentSuccess: Error updating subscription status:', error);
            // Don't retry here, just inform user and log error
            setStatusMessage("We couldn't automatically update your subscription status, but your payment likely succeeded. Please contact support if you encounter issues.");
            setIsError(true);
            setSnackbarOpen(true); // Show error snackbar
        } finally {
            setIsLoadingPage(false);
        }
      } else if (!sessionId) {
          // Landed here without a session ID - maybe direct navigation?
          console.log("PaymentSuccess: No session_id found in URL.");
          setStatusMessage("Payment status unclear. If you just subscribed, your access should be active shortly.");
          setIsError(true); // Treat as an uncertain state
          setIsLoadingPage(false);
      } else {
           // Session already processed or auth still loading on subsequent renders
           // If loading finished and we already processed, keep success message
           if (!isLoadingPage) { 
              // Keep the final status message
           } else {
              // Still waiting for initial load/auth
              setIsLoadingPage(false); 
           }
      }
    };

    processPayment();

  }, [location, user, isAuthLoading, isAuthenticated, navigate, refetchUserData]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Payment Successful | Screamify™</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${backdrop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 0,
          }
        }}
      >
        <Header />
        <Container 
          component="main" 
          maxWidth="sm" 
          sx={{ 
            pt: { xs: 10, md: 16 }, 
            pb: 6, 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Paper
            elevation={10}
            sx={{
              p: { xs: 3, sm: 5 },
              background: 'linear-gradient(135deg, rgba(25, 25, 25, 0.9), rgba(0, 0, 0, 0.9))',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 0, 0, 0.3)',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(255, 0, 0, 0.15), 0 0 10px rgba(255, 0, 0, 0.1)',
              animation: `${fadeIn} 0.8s ease-out`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(to right, #ff0000, #ff5500, #ff0000)',
                animation: `${pulse} 2s infinite ease-in-out`,
              }
            }}
          >
            {isLoadingPage ? (
                <Box sx={{ my: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress 
                      sx={{ 
                        color: '#ff0000',
                        mb: 3,
                        animation: `${pulse} 2s infinite ease-in-out`,
                      }} 
                      size={60}
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mt: 2, 
                        color: 'rgba(255, 255, 255, 0.9)',
                        animation: `${fadeIn} 1s ease-out`,
                      }}
                    >
                      {statusMessage}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ position: 'relative' }}>
                    {!isError && (
                      <CheckCircleOutlineIcon 
                        sx={{ 
                          fontSize: { xs: 60, sm: 80 }, 
                          color: '#4caf50',
                          mb: 2,
                          mt: 1,
                          animation: `${pulse} 2s infinite ease-in-out`,
                        }} 
                      />
                    )}
                    <Typography 
                        variant={isMobile ? "h5" : "h4"} 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: isError ? '#ff9800' : '#4caf50',
                          animation: `${fadeIn} 0.5s ease-out`,
                          mb: 3
                        }}
                    >
                        {isError ? 'Payment Status Update' : 'Payment Successful!'}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mt: 2, 
                        mb: 4, 
                        fontSize: '1.1rem', 
                        whiteSpace: 'pre-line',
                        color: 'rgba(255, 255, 255, 0.9)',
                        animation: `${fadeIn} 0.7s ease-out`,
                        px: { xs: 1, sm: 3 }
                      }}
                    >
                        {statusMessage}
                    </Typography>
                    
                    {!isError && (
                      <Box 
                        sx={{ 
                          mb: 4,
                          animation: `${fadeIn} 0.9s ease-out`,
                          background: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          p: 3,
                          mx: { xs: 0, sm: 2 }
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'medium', 
                            mb: 2, 
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '1rem'
                          }}
                        >
                          Please return to the app and login for access. Thank you for your support!
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 2,
                          mt: 1
                        }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              component="img"
                              src={iosBadge}
                              alt="Download on the App Store"
                              sx={{
                                height: { xs: '36px', sm: '40px' },
                                cursor: 'pointer',
                                transition: 'transform 0.2s, filter 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  filter: 'brightness(1.1)'
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: 0.5 }}>
                              Available Now
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              component="img"
                              src={googlePlayBadge}
                              alt="Get it on Google Play"
                              sx={{
                                height: { xs: '36px', sm: '40px' },
                                cursor: 'pointer',
                                transition: 'transform 0.2s, filter 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  filter: 'brightness(1.1)'
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: 0.5 }}>
                              Available June 1st
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* App hands image */}
                        <Box 
                          sx={{ 
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <Box 
                            component="img"
                            src={appHands}
                            alt="Screamify app on mobile phone"
                            sx={{
                              maxWidth: '100%',
                              height: 'auto',
                              maxHeight: { xs: '220px', sm: '280px' },
                              animation: `${fadeIn} 1.2s ease-out`,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    
                    <Button
                        variant="contained"
                        component={RouterLink} // Use RouterLink for navigation
                        to="/community"
                        size="large"
                        sx={{
                            bgcolor: '#ff0000',
                            backgroundImage: 'linear-gradient(45deg, #ff0000, #cc0000)',
                            py: 1.5,
                            px: 5,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 15px rgba(255, 0, 0, 0.3)',
                            animation: `${fadeIn} 1.1s ease-out`,
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                                bgcolor: '#cc0000',
                                backgroundImage: 'linear-gradient(45deg, #cc0000, #990000)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 7px 20px rgba(255, 0, 0, 0.4)',
                            }
                        }}
                    >
                        GO TO COMMUNITY
                    </Button>
                </Box>
            )}
          </Paper>
        </Container>
         <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity={isError ? 'warning' : 'success'} sx={{ width: '100%' }}>
              {statusMessage} 
            </Alert>
          </Snackbar>
        <Footer />
      </Box>
    </HelmetProvider>
  );
}

export default PaymentSuccess; 